/**
 * Single selectable chat service provider card (ChatGPT, Azure OpenAI, Gemini).
 * Uses images from public/images/apps with letter fallback on load error.
 */
import React, { useState } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import type { ChatServiceProviderOption } from './types';

interface ChatServiceCardProps {
  option: ChatServiceProviderOption;
  onClick: () => void;
}

const ChatServiceCard: React.FC<ChatServiceCardProps> = ({ option, onClick }) => {
  const [imageError, setImageError] = useState(false);
  const showImage = option.imageUrl && !imageError;

  return (
    <Card
      variant="outlined"
      sx={{
        cursor: 'pointer',
        minHeight: 80,
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'action.hover',
        },
      }}
      onClick={onClick}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Provider icon from public/images/apps or fallback to initial letter */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: showImage ? 'transparent' : 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {showImage ? (
            <Box
              component="img"
              src={option.imageUrl}
              alt=""
              sx={{ width: 40, height: 40, objectFit: 'contain' }}
              onError={() => setImageError(true)}
            />
          ) : (
            <Typography variant="body2" fontWeight="bold" color="primary.contrastText">
              {option.label.charAt(0)}
            </Typography>
          )}
        </Box>
        <Typography variant="h6" component="span">
          {option.label}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default ChatServiceCard;
